import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { FIVE_MINUTES, ONE_MINUTE } from '@/constants/time';
import { paths } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

/**
 * Paginated payload returned by the clinical trials endpoint.
 * Includes pagination metadata plus the clinicalTrials array.
 */
export type UserClinicalTrialsData = NonNullable<
  paths['/v2/users/{id}/clinical-trials']['get']['responses'][200]['content']['application/json']['data']
>;

/**
 * A single clinical trial item. title, phase, and status may be null and
 * conditions may be an empty array because the source data is incomplete.
 */
export type ClinicalTrial = UserClinicalTrialsData['clinicalTrials'][number];

/**
 * Fetches a page of the clinical trials an expert is involved in.
 *
 * Requires an authenticated user. Counts for the chip preview come from the
 * profile payload instead. Always responds 200 with a possibly empty list when
 * authorized, so an empty result is the normal state, not an error. Results
 * arrive recency-ordered from the server; no client sort is needed.
 *
 * @param id - Numeric user id of the expert
 * @param page - 1-based page number (default 1)
 * @param limit - Page size, max 50 (default 50, so "Show all" covers the common case)
 * @returns The paginated clinical trials payload, or undefined on a server fault
 */
export async function getUserClinicalTrials(
  id: number,
  page = 1,
  limit = 50,
  signal?: AbortSignal
) {
  const { data } = await fetchClient.GET('/v2/users/{id}/clinical-trials', {
    params: { path: { id }, query: { page, limit } },
    signal,
  });

  return data?.data;
}

/** Endpoint maximum, so the fewest round trips cover a long trial list. */
const ALL_CLINICAL_TRIALS_PAGE_SIZE = 50;

/**
 * Upper bound on pages fetched, 1,000 trials. A runaway `total` must not turn
 * one keystroke into an unbounded request loop.
 */
const MAX_CLINICAL_TRIAL_PAGES = 20;

/**
 * Walks every page of a trial list, up to the page cap, and returns the items in the
 * server's recency order.
 */
async function fetchEveryPage<T>(
  fetchPage: (
    page: number,
    limit: number
  ) => Promise<{ clinicalTrials: T[]; total: number } | undefined>,
  signal?: AbortSignal
): Promise<T[]> {
  const items: T[] = [];

  for (let index = 1; index <= MAX_CLINICAL_TRIAL_PAGES; index++) {
    // A profile the reader has left stops paging instead of finishing the chain.
    signal?.throwIfAborted();

    const page = await fetchPage(index, ALL_CLINICAL_TRIALS_PAGE_SIZE);

    const pageItems = page?.clinicalTrials ?? [];

    items.push(...pageItems);

    if (pageItems.length === 0 || items.length >= (page?.total ?? 0)) {
      break;
    }
  }

  return items;
}

/**
 * Fetches every page of an expert's clinical trials, up to the page cap.
 *
 * @param id - Numeric user id of the expert
 * @returns All trials in the server's recency order, undated trials last
 */
export async function getAllUserClinicalTrials(
  id: number,
  signal?: AbortSignal
): Promise<ClinicalTrial[]> {
  return fetchEveryPage((page, limit) => getUserClinicalTrials(id, page, limit, signal), signal);
}

/**
 * Query options for every one of an expert's clinical trials. The `.mytrials`
 * dot phrase filters this list locally as the expert types, so it needs the
 * whole list rather than a first page.
 *
 * @param id - Numeric user id of the expert
 */
export function getAllUserClinicalTrialsQueryOptions(id: number) {
  return queryOptions({
    queryKey: ['userClinicalTrials', 'all', id],
    queryFn: () => getAllUserClinicalTrials(id),
    staleTime: FIVE_MINUTES,
    gcTime: 10 * ONE_MINUTE,
  });
}

/** The profile's combined trials payload: one page of rows, newest first. */
export type ProfileTrialsData = NonNullable<
  paths['/v2/users/{id}/profile-trials']['get']['responses'][200]['content']['application/json']['data']
>;

/**
 * One row of the profile's combined trials list. `origin` says whether the trial is the
 * expert's own or their institution's; only an own trial can carry a `roleLabel`.
 */
export type ProfileTrialRow = ProfileTrialsData['clinicalTrials'][number];

/**
 * Fetches a page of the profile's combined trials list: the expert's own trials, plus
 * their institution's when `includeInstitutional` is set, merged and sorted newest first
 * by the server.
 *
 * @param id - Numeric user id of the expert
 * @param page - 1-based page number
 * @param limit - Page size, max 50
 * @param includeInstitutional - true when the institutional_trials flag is on
 * @param signal - Aborts the request when the query is abandoned
 */
export async function getUserProfileTrials(
  id: number,
  page: number,
  limit: number,
  includeInstitutional: boolean,
  signal?: AbortSignal
) {
  const { data } = await fetchClient.GET('/v2/users/{id}/profile-trials', {
    params: {
      path: { id },
      query: { page, limit, includeInstitutional: includeInstitutional ? 'true' : 'false' },
    },
    signal,
  });

  return data?.data;
}

/** Page size for the profile's trials list. */
const PROFILE_TRIALS_PAGE_SIZE = 20;

/**
 * Infinite-query options for the profile's combined trials list, a page at a time.
 * Stops once every trial is loaded.
 *
 * @param id - Numeric user id of the expert
 * @param includeInstitutional - true when the institutional_trials flag is on and the
 *   expert has institutional trials to show
 */
export function getProfileTrialsInfiniteQueryOptions(id: number, includeInstitutional: boolean) {
  return infiniteQueryOptions({
    queryKey: ['userClinicalTrials', 'profile', id, includeInstitutional ? 'combined' : 'own'],
    queryFn: ({ pageParam, signal }) =>
      getUserProfileTrials(id, pageParam, PROFILE_TRIALS_PAGE_SIZE, includeInstitutional, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + (page?.clinicalTrials?.length ?? 0), 0);

      return loaded < (lastPage?.total ?? 0) ? allPages.length + 1 : undefined;
    },
    staleTime: FIVE_MINUTES,
    gcTime: 10 * ONE_MINUTE,
  });
}
